/**
 * Sprint T-16.1 · Automation Center · Executions table.
 *
 * Generic paginated table for ExecutionRecord[]. Used by:
 *   - QueueTab (status=pending|retry)
 *   - RunningTab (status=running)
 *   - HistoryTab (all statuses, filterable)
 */
import React from "react";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { StatusBadge, TriggerBadge, RetryBadge, DurationBadge } from "./Badges";
import { RelativeTime, AbsoluteTime } from "./Formatters";

export default function ExecutionsTable({
  items,
  total,
  page,
  pageSize,
  onPageChange,
  onSelectRow,
  testIdPrefix = "executions",
  columns = ["job_name", "status", "attempt", "trigger", "duration", "finished_at", "actor"],
  fetching = false,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div>
      <div className="-mx-1 overflow-x-auto">
        <table className="min-w-full text-xs" data-testid={`${testIdPrefix}-table`}>
          <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
            <tr>
              <th className="px-2 py-2 text-left">ID</th>
              {columns.includes("job_name") && <th className="px-2 py-2 text-left">Job</th>}
              {columns.includes("status") && <th className="px-2 py-2 text-left">Estado</th>}
              {columns.includes("attempt") && <th className="px-2 py-2 text-left">Intento</th>}
              {columns.includes("trigger") && <th className="px-2 py-2 text-left">Trigger</th>}
              {columns.includes("duration") && <th className="px-2 py-2 text-left">Duración</th>}
              {columns.includes("finished_at") && <th className="px-2 py-2 text-left">Terminó</th>}
              {columns.includes("actor") && <th className="px-2 py-2 text-left">Actor</th>}
              <th className="px-2 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e, i) => (
              <tr
                key={e.id || i}
                data-testid={`${testIdPrefix}-row-${i}`}
                onClick={() => onSelectRow?.(e)}
                className="cursor-pointer border-b border-[var(--ag-border)]/60 hover:bg-[var(--ag-surface-2)]/40"
              >
                <td className="px-2 py-1.5 align-top">
                  <code className="font-mono text-[10px] text-[var(--ag-text-dim)]">
                    {(e.id || "").slice(0, 8)}
                  </code>
                </td>
                {columns.includes("job_name") && (
                  <td className="px-2 py-1.5 align-top">
                    <div className="font-mono text-[11px]">{e.job_name}</div>
                  </td>
                )}
                {columns.includes("status") && (
                  <td className="px-2 py-1.5 align-top">
                    <StatusBadge status={e.status} testId={`${testIdPrefix}-status-${i}`} />
                  </td>
                )}
                {columns.includes("attempt") && (
                  <td className="px-2 py-1.5 align-top">
                    <RetryBadge attempt={e.attempt} max={e.max_attempts} testId={`${testIdPrefix}-retry-${i}`} />
                    {(!e.attempt || e.attempt <= 1) && (
                      <span className="font-mono text-[10px] text-[var(--ag-text-dim)]">1</span>
                    )}
                  </td>
                )}
                {columns.includes("trigger") && (
                  <td className="px-2 py-1.5 align-top">
                    <TriggerBadge trigger={e.trigger} testId={`${testIdPrefix}-trigger-${i}`} />
                  </td>
                )}
                {columns.includes("duration") && (
                  <td className="px-2 py-1.5 align-top">
                    <DurationBadge ms={e.duration_ms} testId={`${testIdPrefix}-dur-${i}`} />
                  </td>
                )}
                {columns.includes("finished_at") && (
                  <td className="px-2 py-1.5 align-top">
                    {e.finished_at ? (
                      <RelativeTime iso={e.finished_at} testId={`${testIdPrefix}-fin-${i}`} />
                    ) : (
                      <AbsoluteTime
                        iso={e.available_at || e.created_at}
                        testId={`${testIdPrefix}-avail-${i}`}
                      />
                    )}
                  </td>
                )}
                {columns.includes("actor") && (
                  <td className="px-2 py-1.5 align-top text-[10px] text-[var(--ag-text-dim)]">
                    <span className="truncate block max-w-[160px]" title={e.triggered_by_email}>
                      {e.triggered_by_email || "—"}
                    </span>
                  </td>
                )}
                <td className="px-2 py-1.5 align-top text-right">
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-view-${i}`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onSelectRow?.(e);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-1.5 py-0.5 text-[9px] hover:bg-[var(--ag-surface-2)]"
                  >
                    <Eye className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(total > 0) && (
        <div
          data-testid={`${testIdPrefix}-pagination`}
          className="mt-3 flex items-center justify-between gap-3 text-xs"
        >
          <div className="text-[var(--ag-text-dim)]">
            {(items?.length || 0) > 0
              ? `Mostrando ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)} de ${total}`
              : "sin registros"}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`${testIdPrefix}-page-prev`}
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0 || fetching}
              className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
            >
              <ChevronLeft className="h-3 w-3" /> Anterior
            </button>
            <span className="px-2 text-[10px] tabular-nums">
              pág {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              data-testid={`${testIdPrefix}-page-next`}
              onClick={() => onPageChange?.(page + 1)}
              disabled={(page + 1) * pageSize >= (total || 0) || fetching}
              className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-30"
            >
              Siguiente <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
